// Frontend wrappers for incident-lifecycle callables and Firestore reads.

import { httpsCallable } from 'firebase/functions';
import { getDoc } from 'firebase/firestore';
import { functions } from '../firebase';
import { orgIncidentDoc } from './firestore';
import type { Incident, IncidentEvent, IncidentPriority } from './types';

interface TriggerRequest {
  orgId: string;
  serviceId: string;
  title: string;
  description?: string;
  priority?: IncidentPriority;
}

export async function triggerIncident(
  orgId: string,
  input: { serviceId: string; title: string; description?: string; priority?: IncidentPriority },
): Promise<string> {
  const callable = httpsCallable<TriggerRequest, { incidentId: string }>(
    functions,
    'triggerIncident',
  );
  const result = await callable({ orgId, ...input });
  return result.data.incidentId;
}

export async function acknowledgeIncident(orgId: string, incidentId: string): Promise<void> {
  const callable = httpsCallable(functions, 'acknowledgeIncident');
  await callable({ orgId, incidentId });
}

export async function resolveIncident(orgId: string, incidentId: string): Promise<void> {
  const callable = httpsCallable(functions, 'resolveIncident');
  await callable({ orgId, incidentId });
}

export async function getIncident(orgId: string, incidentId: string): Promise<Incident | null> {
  const snap = await getDoc(orgIncidentDoc(orgId, incidentId));
  if (!snap.exists()) return null;
  return readIncident(snap.data() as IncidentDocShape);
}

type IncidentDocShape = Omit<
  Incident,
  'raisedAt' | 'acknowledgedAt' | 'resolvedAt'
> & {
  raisedAt: number | { toMillis(): number };
  acknowledgedAt: number | null | { toMillis(): number };
  resolvedAt: number | null | { toMillis(): number };
};

function ts(v: number | null | { toMillis(): number }): number | null {
  if (v === null) return null;
  if (typeof v === 'number') return v;
  return v.toMillis();
}

export function readIncident(raw: IncidentDocShape): Incident {
  return {
    ...raw,
    raisedAt: typeof raw.raisedAt === 'number' ? raw.raisedAt : raw.raisedAt.toMillis(),
    acknowledgedAt: ts(raw.acknowledgedAt),
    resolvedAt: ts(raw.resolvedAt),
  };
}

type EventDocShape = Omit<IncidentEvent, 'at'> & {
  at: number | { toMillis(): number };
};

export function readEvent(raw: EventDocShape): IncidentEvent {
  return {
    ...raw,
    at: typeof raw.at === 'number' ? raw.at : raw.at.toMillis(),
  };
}

// ---------- Display helpers ----------

export const STATUS_TONES: Record<
  Incident['status'],
  'danger' | 'warning' | 'success'
> = {
  triggered: 'danger',
  acknowledged: 'warning',
  resolved: 'success',
};

export const STATUS_LABELS: Record<Incident['status'], string> = {
  triggered: 'Triggered',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
};

export const PRIORITY_TONES: Record<IncidentPriority, 'danger' | 'warning' | 'neutral'> = {
  P1: 'danger',
  P2: 'warning',
  P3: 'neutral',
};
